<script setup lang="ts">
/**
 * Base layout container used across the app.
 *
 * Usage patterns:
 * - Dashboard section: default layout with `title` or `#header`, optional `#actions`, and main body in `#content`.
 * - Modal body: place the overlay in a parent element and use `BaseContainer` for the scrollable card body, with header/actions/footer slots.
 * - Horizontal scroll list: set `horizontalScroll` or a flex row `contentLayout` and render cards or tiles inside the `#content` slot.
 *
 * See `app/layout-primitives-usage.md` for canonical layout patterns and page-type recommendations.
 */
import { computed, useSlots } from 'vue'
import { useIsMobile } from '@/composables/useIsMobile'

interface BaseContainerProps {
  title?: string
  padding?: string
  background?: string
  border?: string
  rounded?: string
  shadow?: string
  layout?: string
  contentLayout?: string
  showHeader?: boolean
  showActions?: boolean
  showFilters?: boolean
  horizontalScroll?: boolean
  containerClass?: string
  headerClass?: string
  contentClass?: string
  actionsClass?: string
  filtersClass?: string
  footerClass?: string
}

const props = withDefaults(defineProps<BaseContainerProps>(), {
  title: '',
  padding: 'p-4',
  background: 'bg-surface-raised',
  border: 'border border-border',
  rounded: 'rounded-ui',
  shadow: 'shadow-floating-low',
  layout: 'flex flex-col',
  contentLayout: '',
  showHeader: true,
  showActions: true,
  showFilters: false,
  horizontalScroll: false,
  containerClass: '',
  headerClass: '',
  contentClass: '',
  actionsClass: '',
  filtersClass: '',
  footerClass: '',
})

const { isMobile } = useIsMobile()

// Compute container classes based on props
const containerClasses = computed(() => {
  return [
    props.background,
    props.border,
    props.rounded,
    props.shadow,
    props.padding,
    props.layout,
    props.containerClass,
    'font-normal',
    // Add horizontal scrolling styles when prop is true
    props.horizontalScroll ? 'overflow-x-auto overflow-y-hidden' : '',
  ].join(' ')
})

const headerClasses = computed(() => {
  if (!props.showHeader) return ''
  return [
    'mb-4 flex items-center justify-between border-b border-border pb-2',
    props.headerClass,
  ].join(' ')
})

const contentClasses = computed(() => {
  return [props.contentLayout, props.contentClass].join(' ')
})

const actionsClasses = computed(() => {
  return ['flex gap-2 items-center', props.actionsClass].join(' ')
})

const filtersClasses = computed(() => {
  return ['mb-4', props.filtersClass].join(' ')
})

const slots = useSlots()
if (slots.default) {
  console.warn(
    '[BaseContainer] The default slot is deprecated. Please use the named slot "content" instead.',
  )
}
</script>

<template>
  <section :class="containerClasses">
    <!-- Header Section -->
    <header
      v-if="showHeader"
      :class="headerClasses"
    >
      <div class="header-content">
        <h2
          v-if="title"
          class="text-h3 font-semibold text-on-surface"
        >
          {{ title }}
        </h2>
        <slot
          v-else
          name="header"
        ></slot>
      </div>
      <div
        v-if="showActions && !isMobile"
        :class="actionsClasses"
      >
        <slot name="actions"></slot>
      </div>
    </header>
    <div
      v-if="showActions && isMobile"
      :class="actionsClasses + 'ml-auto'"
    >
      <slot name="actions"></slot>
    </div>

    <!-- Filters Section -->
    <div
      v-if="showFilters"
      :class="filtersClasses"
    >
      <slot name="filters"></slot>
    </div>

    <div>
      <slot name="loading"></slot>
    </div>

    <div>
      <slot name="error"></slot>
    </div>

    <!-- Content Section -->
    <div :class="contentClasses">
      <slot name="content"></slot>
    </div>

    <!-- DEPRECATED. USE <slot name="content"></slot> -->
    <div v-if="slots.default">
      <slot></slot>
    </div>

    <!-- Empty State Slot -->
    <slot name="empty"></slot>

    <div
      v-if="showActions"
      :class="actionsClasses + ' mt-2 flex justify-end'"
    >
      <slot name="bottomActions"></slot>
    </div>

    <!-- Footer Section -->
    <footer
      v-if="$slots.footer"
      :class="[
        'mt-4 border-t border-border pt-4 text-sm text-text-secondary',
        props.footerClass,
      ]"
    >
      <slot name="footer"></slot>
    </footer>
  </section>
</template>

<style scoped>
/* Scoped styles if needed, but primarily using Tailwind */
</style>
